/**
 * Sales Orders Section Component
 */

import { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react'
import { useSelector, useDispatch, useUrlFilterSync, useSearch, useNavigate, useTranslation, useForm, zodResolver, z } from '@spcc/core'
import { exportToXlsx, type ExportColumn } from '@spcc/core'
import {
  PaginationControls,
  useToast,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
} from '@spcc/uikit'
import { selectIsAdmin, selectSelectedTenant } from '@spcc/domains/admin-context'
import { selectUserEmail, selectUserRoleType, isReadOnlyAdminRole } from '@spcc/domains/auth'
import { selectFactoriesList, selectFactoriesLoading, factoriesActions } from '@spcc/domains/factories'
import { salesOrdersApi } from '../../../infrastructure/api/sales-orders-api'
import { salesOrdersFilterConfig, DEFAULT_SALES_ORDERS_FILTER } from '../../../domain/filter-config'
import {
  selectFilteredSalesOrders,
  selectSalesOrderStats,
  selectSalesOrdersLoading,
  selectSalesOrdersError,
  selectSalesOrdersFilter,
  selectSalesOrdersPagination,
  selectSalesOrdersSkuOptions,
  selectSalesOrdersSkuOptionsLoading,
  selectSalesOrdersSkuOptionsLoaded,
} from '../../../store/sales-orders.selectors'
import { salesOrdersActions } from '../../../store/sales-orders.slice'
import { useSalesOrdersReadable } from '../../../copilot/sales-orders.readable'
import { useSalesOrdersActions } from '../../../copilot/sales-orders.actions'
import { useSalesOrdersSuggestions } from '../../../copilot/sales-orders.suggestions'
import { addressbookApi } from '@spcc/domains/addressbook'
const SalesOrderDetailDrawer = lazy(() => import('../../drawers/sales-order-detail-drawer').then(m => ({ default: m.SalesOrderDetailDrawer })))
const SalesOrderFormDrawer = lazy(() => import('../../drawers/sales-order-form-drawer').then(m => ({ default: m.SalesOrderFormDrawer })))
import type { SalesOrder, SalesOrderStatus, SalesOrdersFilter, SalesOrdersSortBy, SalesOrderSkuOption } from '../../../domain/types'
import { SalesOrdersHeader, SalesOrdersStats, SalesOrdersToolbarV2, SalesOrdersTable, SalesOrdersCardView, SalesOrdersEmptyState, CancelDialog } from './components'

// ─── Helper functions ─────────────────────────────────────────────────────────

type DeliveryAddressOption = { value: string; label: string }

type TranslateFn = ReturnType<typeof useTranslation>['t']

function buildSkuOptions(options: SalesOrderSkuOption[], selectedCodes: string[]): SalesOrderSkuOption[] {
  const byKey = new Map<string, SalesOrderSkuOption>()
  for (const option of options) {
    const key = option.variantId || option.code
    if (key && !byKey.has(key)) byKey.set(key, option)
  }
  for (const code of selectedCodes) {
    if (!byKey.has(code)) byKey.set(code, { code, label: code, variantId: code })
  }
  return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label))
}

function buildStatusToggleFilter(filter: SalesOrdersFilter, status: SalesOrderStatus): SalesOrdersFilter {
  return {
    ...DEFAULT_SALES_ORDERS_FILTER,
    factoryIds: filter.factoryIds,
    pageSize: filter.pageSize,
    status: [status],
  }
}

interface SalesOrderExportEmailFormValues {
  email: string
}

function createSalesOrderExportEmailSchema(t: TranslateFn) {
  return z.object({
    email: z
      .string()
      .trim()
      .min(1, t('salesOrders.export.emailRequired', { defaultValue: 'Email is required' }))
      .email(t('salesOrders.export.emailInvalid', { defaultValue: 'Please enter a valid email address' })),
  })
}

function getInitialSalesOrderExportEmail(userEmail: string | null): string {
  return userEmail?.trim() ?? ''
}

function getSalesOrderExportColumns(isAdmin: boolean): ExportColumn<SalesOrder>[] {
  const columns: ExportColumn<SalesOrder>[] = [
    { header: 'Sales Order #', key: 'orderNumber', width: 20 },
    { header: 'Status', key: 'status', width: 14 },
    { header: '# of Orders', accessor: (so) => so.ordersCount?.total ?? 0, width: 14 },
    { header: 'Products', accessor: (so) => so.lines.map(l => l.product?.en ?? '').filter(Boolean).join(', '), width: 30 },
    { header: 'Plant', accessor: (so) => so.factoryName ?? '', width: 18 },
  ]
  if (isAdmin) columns.unshift({ header: 'Customer', accessor: (so) => so.tenant?.name ?? '', width: 20 })
  return columns
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SalesOrdersSection() {
  const dispatch = useDispatch()
  const { toast } = useToast()
  const { t, i18n } = useTranslation()

  const salesOrders = useSelector(selectFilteredSalesOrders)
  const stats = useSelector(selectSalesOrderStats)
  const loading = useSelector(selectSalesOrdersLoading)
  const error = useSelector(selectSalesOrdersError)
  const filter = useSelector(selectSalesOrdersFilter)
  const pagination = useSelector(selectSalesOrdersPagination)
  const skuOptions = useSelector(selectSalesOrdersSkuOptions)
  const skuOptionsLoading = useSelector(selectSalesOrdersSkuOptionsLoading)
  const skuOptionsLoaded = useSelector(selectSalesOrdersSkuOptionsLoaded)
  const isAdmin = useSelector(selectIsAdmin)
  const roleType = useSelector(selectUserRoleType)
  const isReadOnly = isReadOnlyAdminRole(roleType)
  const selectedTenant = useSelector(selectSelectedTenant)
  const factoriesList = useSelector(selectFactoriesList)
  const factoriesLoading = useSelector(selectFactoriesLoading)
  const userEmail = useSelector(selectUserEmail)

  const { setFilter: setUrlFilter, activeFilterCount } = useUrlFilterSync({
    config: salesOrdersFilterConfig,
    currentFilter: filter,
    onFilterChange: useCallback(
      (newFilter: SalesOrdersFilter) => dispatch(salesOrdersActions.setFilter(newFilter)),
      [dispatch]
    ),
    debounceMs: 300,
    initFromUrl: false,
  })

  const [searchValue, setSearchValue] = useState(() => filter.search ?? '')
  const [selectedSalesOrder, setSelectedSalesOrder] = useState<SalesOrder | null>(null)
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null)
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false)
  const [formDrawerOpen, setFormDrawerOpen] = useState(false)
  const [editingSalesOrder, setEditingSalesOrder] = useState<SalesOrder | null>(null)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [exportSubmitting, setExportSubmitting] = useState(false)
  const [salesOrderToCancel, setSalesOrderToCancel] = useState<SalesOrder | null>(null)
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table')
  const [availableAddresses, setAvailableAddresses] = useState<DeliveryAddressOption[]>([])
  const [addressSearch, setAddressSearch] = useState('')
  const [addressesLoading, setAddressesLoading] = useState(false)
  const [prevLoading, setPrevLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const isListLoading = loading && !isSaving

  const productOptions = useMemo(
    () => buildSkuOptions(skuOptions, filter.productCodes ?? []),
    [skuOptions, filter.productCodes]
  )

  const deliveryAddressOptions = useMemo<DeliveryAddressOption[]>(() => {
    const byValue = new Map(availableAddresses.map(a => [a.value, a]))
    for (const addr of filter.deliveryAddresses ?? []) {
      if (!byValue.has(addr)) byValue.set(addr, { value: addr, label: addr })
    }
    return [...byValue.values()].sort((a, b) => a.label.localeCompare(b.label))
  }, [availableAddresses, filter.deliveryAddresses])

  const factoryOptions = useMemo(
    () => factoriesList.map(f => ({ value: f.documentId, label: f.name })).sort((a, b) => a.label.localeCompare(b.label)),
    [factoriesList]
  )

  // Clear stale data when language changes so the loading spinner appears immediately
  const prevLangRef = useRef(i18n.language)
  useEffect(() => {
    if (prevLangRef.current !== i18n.language) {
      prevLangRef.current = i18n.language
      dispatch(salesOrdersActions.clearSalesOrders())
    }
  }, [dispatch, i18n.language])

  // Fetch when filter or language changes
  useEffect(() => {
    dispatch(salesOrdersActions.fetchSalesOrders(filter))
  }, [dispatch, filter, i18n.language])

  // Refresh stats in background (respects plant filter and language)
  useEffect(() => {
    dispatch(salesOrdersActions.fetchSalesOrderStats())
  }, [dispatch, filter.factoryIds, i18n.language])

  // Fetch SKU options once
  useEffect(() => {
    if (!skuOptionsLoaded && !skuOptionsLoading) dispatch(salesOrdersActions.fetchSkuOptions())
  }, [dispatch, skuOptionsLoaded, skuOptionsLoading])

  // Fetch factories list for filter dropdown
  useEffect(() => {
    if (factoriesList.length === 0 && !factoriesLoading) dispatch(factoriesActions.fetchFactoriesList())
  }, [dispatch, factoriesList.length, factoriesLoading])

  // Auto-open create drawer from URL action param
  const searchParams = useSearch({ strict: false }) as Record<string, string | undefined>
  const navigate = useNavigate()
  useEffect(() => {
    if (searchParams?.action === 'new' && !formDrawerOpen) {
      setEditingSalesOrder(null)
      setFormDrawerOpen(true)
      navigate({ search: {} as any, replace: true })
    }
  }, [searchParams?.action, formDrawerOpen, navigate])

  // Fetch addresses for filter dropdown (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      setAddressesLoading(true)
      addressbookApi.getAddresses({ search: addressSearch })
        .then(addresses => {
          setAvailableAddresses(
            addresses.map(a => ({ value: a.documentId || String((a as any).id), label: a.name }))
              .sort((a, b) => a.label.localeCompare(b.label))
          )
        })
        .catch(err => console.error('Failed to fetch addresses for filters:', err))
        .finally(() => setAddressesLoading(false))
    }, 500)
    return () => clearTimeout(timer)
  }, [addressSearch])

  // Sync search input when filter is reset externally
  useEffect(() => {
    const normalized = filter.search ?? ''
    if (normalized !== searchValue) setSearchValue(normalized)
  }, [filter.search])

  // Close form drawer when save succeeds
  useEffect(() => {
    if (isSaving && prevLoading && !loading) {
      if (!error && formDrawerOpen) {
        setFormDrawerOpen(false)
        setEditingSalesOrder(null)
        dispatch(salesOrdersActions.clearError())
        setIsSaving(false)
      } else if (error) {
        setIsSaving(false)
        if (!formDrawerOpen) setFormDrawerOpen(true)
      }
    }
    setPrevLoading(loading)
  }, [loading, error, formDrawerOpen, prevLoading, isSaving, dispatch])

  // Keep drawer open if error occurs during save
  useEffect(() => {
    if (error && !formDrawerOpen && isSaving) setFormDrawerOpen(true)
  }, [error, formDrawerOpen, isSaving])

  const exportEmailSchema = useMemo(() => createSalesOrderExportEmailSchema(t), [t])
  const {
    register: registerExportEmail,
    handleSubmit: handleExportEmailSubmit,
    reset: resetExportEmailForm,
    setValue: setExportEmailValue,
    formState: { errors: exportEmailErrors },
  } = useForm<SalesOrderExportEmailFormValues>({
    resolver: zodResolver(exportEmailSchema),
    defaultValues: { email: getInitialSalesOrderExportEmail(userEmail) },
  })

  useEffect(() => {
    if (!exportDialogOpen) return
    resetExportEmailForm({ email: getInitialSalesOrderExportEmail(userEmail) })
  }, [exportDialogOpen, resetExportEmailForm, userEmail])

  useSalesOrdersReadable()
  useSalesOrdersActions()
  useSalesOrdersSuggestions()

  // ─── Pagination (dispatch directly to Redux for reliable page changes) ───────

  const handlePageChange = useCallback((page: number) => {
    dispatch(salesOrdersActions.setFilter({ ...filter, page }))
  }, [dispatch, filter])

  const handlePageSizeChange = useCallback((pageSize: number) => {
    dispatch(salesOrdersActions.setFilter({ ...filter, pageSize, page: 1 }))
  }, [dispatch, filter])

  const handleColumnSort = useCallback((field: SalesOrdersSortBy) => {
    const newOrder = filter.sortBy === field && filter.sortOrder === 'desc' ? 'asc' : 'desc'
    dispatch(salesOrdersActions.setFilter({ ...filter, sortBy: field, sortOrder: newOrder, page: 1 }))
  }, [dispatch, filter])

  // ─── Row / selection handlers ─────────────────────────────────────────────

  const handleRowClick = useCallback((salesOrder: SalesOrder) => {
    setSelectedRowId(salesOrder.id)
    setSelectedSalesOrder(salesOrder)
    dispatch(salesOrdersActions.setSelectedSalesOrder(salesOrder))
    setViewDrawerOpen(true)
  }, [dispatch])

  const handleRowDoubleClick = useCallback((salesOrder: SalesOrder) => {
    if (selectedRowId === salesOrder.id) {
      setSelectedRowId(null)
      setSelectedSalesOrder(null)
      setViewDrawerOpen(false)
    }
  }, [selectedRowId])

  const handleCreateNew = useCallback(() => {
    setEditingSalesOrder(null)
    setFormDrawerOpen(true)
  }, [])

  const handleEditSalesOrder = useCallback((salesOrder: SalesOrder) => {
    setEditingSalesOrder(salesOrder)
    setFormDrawerOpen(true)
  }, [])

  const handleCancelClick = useCallback((salesOrder: SalesOrder) => {
    setSalesOrderToCancel(salesOrder)
    setCancelDialogOpen(true)
  }, [])

  const handleConfirmCancel = useCallback(() => {
    if (salesOrderToCancel) {
      dispatch(salesOrdersActions.cancelSalesOrder({ salesOrderId: salesOrderToCancel.id }))
      setCancelDialogOpen(false)
      setSalesOrderToCancel(null)
    }
  }, [dispatch, salesOrderToCancel])

  const handleReorder = useCallback((salesOrder: SalesOrder) => {
    setEditingSalesOrder({
      ...salesOrder,
      id: '',
      orderNumber: '',
      status: 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      _originalNumber: salesOrder.orderNumber,
    } as SalesOrder)
    setFormDrawerOpen(true)
  }, [])

  const handleStatusQuickToggle = useCallback((status: SalesOrderStatus) => {
    dispatch(salesOrdersActions.setFilter(buildStatusToggleFilter(filter, status)))
  }, [dispatch, filter])

  // ─── Toolbar handlers (go through URL sync for shareable URLs) ────────────

  const handleToolbarSearchChange = useCallback((value: string) => {
    setSearchValue(value)
    setUrlFilter({ search: value, page: 1 })
  }, [setUrlFilter])

  const handleToolbarStatusChange = useCallback((statuses: SalesOrderStatus[]) => {
    setUrlFilter({ status: statuses, page: 1 })
  }, [setUrlFilter])

  const handleToolbarProductCodesChange = useCallback((codes: string[]) => {
    setUrlFilter({ productCodes: codes, page: 1 })
  }, [setUrlFilter])

  const handleToolbarDeliveryAddressesChange = useCallback((addresses: string[]) => {
    setUrlFilter({ deliveryAddresses: addresses, page: 1 })
  }, [setUrlFilter])

  const handleToolbarMinValueChange = useCallback((value: number | undefined) => {
    setUrlFilter({ minValue: value, page: 1 })
  }, [setUrlFilter])

  const handleToolbarMaxValueChange = useCallback((value: number | undefined) => {
    setUrlFilter({ maxValue: value, page: 1 })
  }, [setUrlFilter])

  const handleToolbarDateRangeChange = useCallback((range: { from?: string; to?: string } | undefined) => {
    setUrlFilter({ dateRange: range, page: 1 })
  }, [setUrlFilter])

  const handleToolbarCreatedDateRangeChange = useCallback((range: { from?: string; to?: string } | undefined) => {
    setUrlFilter({ createdDateRange: range, page: 1 })
  }, [setUrlFilter])

  const handleToolbarApplyAllFilters = useCallback((filters: {
    status?: SalesOrderStatus[]
    productCodes?: string[]
    deliveryAddresses?: string[]
    minValue?: number
    maxValue?: number
    dateRange?: { from?: string; to?: string }
    createdDateRange?: { from?: string; to?: string }
  }) => {
    setUrlFilter({ ...filters, page: 1 })
  }, [setUrlFilter])

  const handleClearFilters = useCallback(() => {
    setSearchValue('')
    dispatch(salesOrdersActions.setFilter(DEFAULT_SALES_ORDERS_FILTER))
  }, [dispatch])

  // ─── Drawer handlers ──────────────────────────────────────────────────────

  const handleViewDrawerOpenChange = useCallback((open: boolean) => {
    setViewDrawerOpen(open)
    if (!open) setSelectedRowId(null)
  }, [])

  const handleViewDrawerReorder = useCallback((contract: SalesOrder) => {
    setViewDrawerOpen(false)
    handleReorder(contract)
  }, [handleReorder])

  const handleFormDrawerOpenChange = useCallback((open: boolean) => {
    if (!open && (error || isSaving || loading)) return
    if (!open) {
      setFormDrawerOpen(false)
      setEditingSalesOrder(null)
      dispatch(salesOrdersActions.clearError())
      setIsSaving(false)
    } else {
      setFormDrawerOpen(true)
    }
  }, [error, isSaving, loading, dispatch])

  const handleFormDrawerSave = useCallback((data: Record<string, unknown>) => {
    dispatch(salesOrdersActions.clearError())
    setIsSaving(true)
    if (editingSalesOrder?.id) {
      dispatch(salesOrdersActions.updateSalesOrder({ id: editingSalesOrder.id, ...data }))
    } else {
      dispatch(salesOrdersActions.createSalesOrder(data))
    }
  }, [dispatch, editingSalesOrder])

  const handleOpenExportDialog = useCallback(() => {
    setExportEmailValue('email', getInitialSalesOrderExportEmail(userEmail))
    setExportDialogOpen(true)
  }, [setExportEmailValue, userEmail])

  const handleExportDialogOpenChange = useCallback((open: boolean) => {
    setExportDialogOpen(open)
    if (!open) resetExportEmailForm({ email: getInitialSalesOrderExportEmail(userEmail) })
  }, [resetExportEmailForm, userEmail])

  const submitSalesOrderExport = handleExportEmailSubmit(async (values) => {
    try {
      setExportSubmitting(true)
      toast({
        title: t('salesOrders.export.exportingTitle', { defaultValue: 'Exporting...' }),
        description: t('salesOrders.export.exportingDesc', { defaultValue: 'Fetching all records' }),
      })

      const result = await salesOrdersApi.exportAll({ filter, email: values.email.trim(), locale: i18n.language })

      if (result.mode === 'email') {
        toast({
          title: t('salesOrders.export.exportEmailedTitle', { defaultValue: 'Export will be emailed' }),
          description: t('salesOrders.export.exportEmailedDescription', {
            email: result.email,
            count: result.count,
            defaultValue: `We will send ${result.count} sales orders to ${result.email}`,
          }),
        })
        handleExportDialogOpenChange(false)
        return
      }

      exportToXlsx({
        fileName: 'sales-orders',
        sheetName: 'Sales Orders',
        columns: getSalesOrderExportColumns(isAdmin),
        rows: result.orders,
      })

      toast({
        title: t('salesOrders.export.exportDownloadedTitle', { defaultValue: 'Export downloaded' }),
        description: t('salesOrders.export.exportDownloadedDescription', {
          count: result.count,
          defaultValue: `Downloaded ${result.count} sales orders`,
        }),
      })
      handleExportDialogOpenChange(false)
    } catch (error) {
      console.error('Export failed:', error)
      toast({
        title: t('salesOrders.export.exportFailedTitle', { defaultValue: 'Export failed' }),
        description: t('salesOrders.export.exportFailed', { defaultValue: 'Could not export sales orders' }),
        variant: 'destructive',
      })
    } finally {
      setExportSubmitting(false)
    }
  })

  // ─── Render ───────────────────────────────────────────────────────────────

  if (isListLoading && salesOrders.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6" data-testid="sales-orders-section">
      <SalesOrdersHeader
        onCreateNew={handleCreateNew}
        onExport={handleOpenExportDialog}
        createDisabled={isReadOnly}
        createDisabledReason={isReadOnly ? t('salesOrders.readOnlyAccess') : undefined}
      />

      <SalesOrdersStats stats={stats} filter={filter} onStatusFilter={handleStatusQuickToggle} />

      <SalesOrdersToolbarV2
        searchValue={searchValue}
        onSearchChange={handleToolbarSearchChange}
        statusFilter={filter.status ?? []}
        onStatusChange={handleToolbarStatusChange}
        productCodesFilter={filter.productCodes ?? []}
        onProductCodesChange={handleToolbarProductCodesChange}
        productOptions={productOptions}
        productOptionsLoading={skuOptionsLoading}
        deliveryAddressesFilter={filter.deliveryAddresses ?? []}
        onDeliveryAddressesChange={handleToolbarDeliveryAddressesChange}
        deliveryAddressOptions={deliveryAddressOptions}
        minValue={filter.minValue}
        maxValue={filter.maxValue}
        onMinValueChange={handleToolbarMinValueChange}
        onMaxValueChange={handleToolbarMaxValueChange}
        dateRange={filter.dateRange}
        onDateRangeChange={handleToolbarDateRangeChange}
        createdDateRange={filter.createdDateRange}
        onCreatedDateRangeChange={handleToolbarCreatedDateRangeChange}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        isLoading={isListLoading}
        activeFilterCount={activeFilterCount}
        onClearFilters={handleClearFilters}
        onApplyAllFilters={handleToolbarApplyAllFilters}
      />

      {salesOrders.length === 0 ? (
        <SalesOrdersEmptyState
          onCreateNew={handleCreateNew}
          createDisabled={isReadOnly}
          createDisabledReason={isReadOnly ? t('salesOrders.readOnlyAccess') : undefined}
        />
      ) : viewMode === 'table' ? (
        <SalesOrdersTable
          salesOrders={salesOrders}
          selectedRowId={selectedRowId}
          showTenantColumn={isAdmin}
          sortField={filter.sortBy}
          sortOrder={filter.sortOrder}
          onSort={handleColumnSort}
          onRowClick={handleRowClick}
          onRowDoubleClick={handleRowDoubleClick}
          onView={handleRowClick}
          onEdit={isReadOnly ? undefined : handleEditSalesOrder}
          onReorder={isReadOnly ? undefined : handleReorder}
          onCancel={isReadOnly ? undefined : handleCancelClick}
        />
      ) : (
        <SalesOrdersCardView
          salesOrders={salesOrders}
          showTenantColumn={isAdmin}
          onView={handleRowClick}
          onReorder={isReadOnly ? undefined : handleReorder}
          onCancel={isReadOnly ? undefined : handleCancelClick}
        />
      )}

      {salesOrders.length > 0 && (
        <PaginationControls
          page={pagination.page}
          pageSize={pagination.pageSize}
          totalItems={pagination.total}
          totalPages={pagination.pageCount}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizes={[10, 20, 50, 100]}
        />
      )}

      <Suspense fallback={null}>
        <SalesOrderDetailDrawer
          contract={selectedSalesOrder}
          open={viewDrawerOpen}
          onOpenChange={handleViewDrawerOpenChange}
          onReorder={isReadOnly ? undefined : handleViewDrawerReorder}
        />
      </Suspense>

      <Suspense fallback={null}>
        <SalesOrderFormDrawer
          contract={editingSalesOrder}
          open={formDrawerOpen}
          isSaving={isSaving}
          onOpenChange={handleFormDrawerOpenChange}
          onSave={handleFormDrawerSave}
        />
      </Suspense>

      <CancelDialog
        open={cancelDialogOpen}
        salesOrder={salesOrderToCancel}
        onOpenChange={setCancelDialogOpen}
        onConfirm={handleConfirmCancel}
      />

      <Dialog open={exportDialogOpen} onOpenChange={handleExportDialogOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('salesOrders.export.emailPromptTitle', { defaultValue: 'Where should we send the export?' })}
            </DialogTitle>
            <DialogDescription>
              {t('salesOrders.export.emailPromptDescription', {
                defaultValue: 'Enter the email address for this export. Small exports download immediately after submit, and larger exports are sent by email.',
              })}
            </DialogDescription>
          </DialogHeader>

          <form id="sales-order-export-email-form" onSubmit={submitSalesOrderExport} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sales-order-export-email">
                {t('salesOrders.export.emailLabel', { defaultValue: 'Email address' })}
              </Label>
              <Input
                id="sales-order-export-email"
                type="email"
                autoFocus
                placeholder={t('salesOrders.export.emailPlaceholder', { defaultValue: 'name@example.com' })}
                {...registerExportEmail('email')}
              />
              {exportEmailErrors.email?.message && (
                <p className="text-xs text-destructive">
                  {exportEmailErrors.email.message}
                </p>
              )}
            </div>
          </form>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleExportDialogOpenChange(false)}
              disabled={exportSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="sales-order-export-email-form"
              disabled={exportSubmitting}
            >
              {exportSubmitting && <Spinner className="me-2 h-4 w-4" />}
              {t('common.export')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export { SalesOrdersSection as ContractsSection }
